import React, { useState } from "react";
import { api } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Badge } from "@/components/ui/badge";
import { toast } from "sonner";
import { Rocket, Loader2, TrendingUp, Star, Zap } from "lucide-react";

// Mirrors backend BOOST_PRICES — kept small so we don't need a network call
// just to render the price picker. Backend is the source of truth for verification.
const OPTIONS = [
  { days: 7, price: 499, icon: Zap, tagline: "Try it out", popular: false },
  { days: 15, price: 899, icon: TrendingUp, tagline: "Best for exam season", popular: true },
  { days: 30, price: 1499, icon: Star, tagline: "Maximum visibility", popular: false },
];

async function loadRazorpayScript() {
  if (window.Razorpay) return true;
  return new Promise((resolve) => {
    const s = document.createElement("script");
    s.src = "https://checkout.razorpay.com/v1/checkout.js";
    s.onload = () => resolve(true);
    s.onerror = () => resolve(false);
    document.body.appendChild(s);
  });
}

export default function BoostListingDialog({ hostel, open, onClose, onBoosted }) {
  const [selected, setSelected] = useState(15);
  const [processing, setProcessing] = useState(false);

  const currentBoostActive = hostel?.featured_until && new Date(hostel.featured_until) > new Date();
  const currentUntil = currentBoostActive ? new Date(hostel.featured_until) : null;

  const startBoost = async () => {
    setProcessing(true);
    try {
      const ok = await loadRazorpayScript();
      if (!ok) throw new Error("Could not load Razorpay checkout");
      const { data: order } = await api.post(`/owner/hostels/${hostel.id}/boost/order`, { days: selected });

      const rzp = new window.Razorpay({
        key: order.key_id,
        amount: order.amount,
        currency: order.currency,
        name: "TheHostelNow.com",
        description: `Featured listing · ${selected} days · ${hostel.name}`,
        order_id: order.order_id,
        theme: { color: "#9A3412" },
        handler: async (resp) => {
          try {
            const { data: v } = await api.post(`/owner/hostels/${hostel.id}/boost/verify`, {
              days: selected,
              razorpay_order_id: resp.razorpay_order_id,
              razorpay_payment_id: resp.razorpay_payment_id,
              razorpay_signature: resp.razorpay_signature,
            });
            toast.success(`Boost live! Featured until ${new Date(v.featured_until).toLocaleDateString("en-IN")}`);
            onBoosted?.(v);
            onClose?.();
          } catch (e) {
            toast.error(e.response?.data?.detail || "Boost verification failed");
          } finally {
            setProcessing(false);
          }
        },
        modal: {
          ondismiss: () => setProcessing(false),
        },
        prefill: {},
      });
      rzp.on("payment.failed", (resp) => {
        toast.error(`Payment failed: ${resp.error?.description || "unknown"}`);
        setProcessing(false);
      });
      rzp.open();
    } catch (e) {
      toast.error(e.response?.data?.detail || e.message || "Could not start payment");
      setProcessing(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={(v) => !v && !processing && onClose?.()}>
      <DialogContent data-testid="boost-dialog" className="max-w-2xl">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <Rocket size={18} className="text-vermilion-600" />
            Boost your listing — {hostel?.name}
          </DialogTitle>
        </DialogHeader>
        {currentBoostActive && (
          <div className="rounded-lg bg-emerald-50 border border-emerald-200 px-3 py-2 text-xs text-emerald-900">
            ✅ Currently featured until <b>{currentUntil.toLocaleString("en-IN")}</b>. Any new boost you buy now will <b>stack</b> on top.
          </div>
        )}
        <p className="text-sm text-slate-600">
          Featured hostels appear at the <b>top of every search result</b> with a <b>Sponsored</b> badge — proven to lift bookings by 3-6×.
        </p>
        <div className="grid gap-3 sm:grid-cols-3">
          {OPTIONS.map(({ days, price, icon: Icon, tagline, popular }) => {
            const active = selected === days;
            return (
              <button
                type="button"
                key={days}
                data-testid={`boost-option-${days}`}
                onClick={() => setSelected(days)}
                className={`relative text-left border-2 rounded-xl p-4 transition-colors ${active ? "border-vermilion-600 bg-vermilion-50" : "border-slate-200 hover:border-slate-300 bg-white"}`}
              >
                {popular && (
                  <Badge className="absolute -top-2 -right-2 bg-turmeric-400 text-jodhpur-900 text-[9px] tracking-widest uppercase">Popular</Badge>
                )}
                <Icon size={18} className={active ? "text-vermilion-700" : "text-slate-500"} />
                <div className="mt-2 font-display font-black text-2xl leading-none">{days}<span className="text-sm font-bold text-slate-500"> days</span></div>
                <div className="mt-1 font-bold text-vermilion-700">₹{price.toLocaleString("en-IN")}</div>
                <div className="text-[11px] text-slate-500 mt-1">{tagline}</div>
                <div className="text-[10px] text-slate-400 mt-2">~ ₹{Math.round(price / days)}/day</div>
              </button>
            );
          })}
        </div>
        <div className="flex justify-end gap-2 flex-wrap">
          <Button variant="outline" onClick={onClose} disabled={processing} data-testid="boost-cancel-btn">Cancel</Button>
          <Button
            data-testid="boost-pay-btn"
            onClick={startBoost}
            disabled={processing}
            className="bg-vermilion-600 hover:bg-vermilion-700 text-white gap-2"
          >
            {processing ? <Loader2 size={14} className="animate-spin" /> : <Rocket size={14} />}
            {processing ? "Opening payment…" : `Pay ₹${OPTIONS.find(o => o.days === selected).price.toLocaleString("en-IN")} & boost`}
          </Button>
        </div>
        <div className="text-[10px] text-slate-500 leading-relaxed">
          Payment is processed securely via Razorpay. Featured status starts the instant your payment succeeds and continues for the duration you selected. All boost purchases are non-refundable once the boost period begins.
        </div>
      </DialogContent>
    </Dialog>
  );
}
